import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';

import { ALIYUN_CAPTCHA_SDK } from '../utils/aliyunCaptchaSdk';

const ALIYUN_CAPTCHA_APP_ID = '0785b2eda8b5e942b90813cbd6b3ea23';
const CAPTCHA_ROOT_ID = 'aliyun-captcha-root';

export type AliyunCaptchaResult = {
  captchaId?: string;
  captchaOutput?: string;
  lotNumber?: string;
  passToken?: string;
  genTime?: string;
  validate?: string;
};

type AliyunCaptchaModalProps = {
  visible: boolean;
  onClose: () => void;
  onSuccess: (payload: AliyunCaptchaResult) => void;
  onError?: (message: string) => void;
};

type CaptchaInstance = {
  appendTo: (selector: string) => CaptchaInstance;
  onReady: (callback: () => void) => CaptchaInstance;
  onError: (callback: (error?: { msg?: string }) => void) => CaptchaInstance;
  onSuccess: (callback: () => void) => CaptchaInstance;
  onClose?: (callback: () => void) => void;
  showCaptcha?: () => void;
  hide?: () => void;
  destroy?: () => void;
  getValidate?: () => Record<string, string> | null;
};

declare global {
  interface Window {
    initAlicom4?: (
      config: Record<string, string>,
      callback: (captchaObj: CaptchaInstance | null) => void,
    ) => void;
  }
}

function ensureCaptchaSdkLoaded() {
  if (typeof window.initAlicom4 === 'function') {
    return Promise.resolve();
  }

  const existing = document.getElementById('aliyun-captcha-sdk-script');
  if (existing) {
    return typeof window.initAlicom4 === 'function'
      ? Promise.resolve()
      : Promise.reject(new Error('图形验证码 SDK 不可用'));
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'aliyun-captcha-sdk-script';
    script.text = ALIYUN_CAPTCHA_SDK.replace(/<\/script/gi, '<\\/script');
    script.onerror = () => reject(new Error('图形验证码 SDK 加载失败'));
    document.body.appendChild(script);

    if (typeof window.initAlicom4 === 'function') {
      resolve();
      return;
    }

    reject(new Error('图形验证码 SDK 不可用'));
  });
}

export function AliyunCaptchaModal({ visible, onClose, onSuccess, onError }: AliyunCaptchaModalProps) {
  const [ready, setReady] = useState(false);
  const resolvedRef = useRef(false);
  const captchaRef = useRef<CaptchaInstance | null>(null);

  useEffect(() => {
    if (!visible) {
      setReady(false);
      resolvedRef.current = false;
      captchaRef.current?.destroy?.();
      captchaRef.current = null;
      return;
    }

    let cancelled = false;
    resolvedRef.current = false;
    setReady(false);

    void (async () => {
      try {
        await ensureCaptchaSdkLoaded();
        if (cancelled || typeof window.initAlicom4 !== 'function') {
          throw new Error('图形验证码 SDK 不可用');
        }

        window.initAlicom4(
          {
            captchaId: ALIYUN_CAPTCHA_APP_ID,
            appId: ALIYUN_CAPTCHA_APP_ID,
            protocol: 'https://',
            product: 'bind',
            language: 'zh-CN',
          },
          (captchaObj) => {
            if (cancelled) {
              return;
            }

            if (!captchaObj) {
              onError?.('图形验证码初始化失败');
              onClose();
              return;
            }

            const instance = captchaObj;
            captchaRef.current = instance;

            function closeWithError(message: string) {
              if (resolvedRef.current) {
                return;
              }
              resolvedRef.current = true;
              onError?.(message);
              instance.hide?.();
              instance.destroy?.();
              onClose();
            }

            instance
              .appendTo(`#${CAPTCHA_ROOT_ID}`)
              .onReady(() => {
                if (cancelled) {
                  return;
                }
                setReady(true);
                instance.showCaptcha?.();
              })
              .onError((error) => {
                closeWithError(error?.msg || '图形验证码校验失败');
              })
              .onSuccess(() => {
                if (resolvedRef.current) {
                  return;
                }

                const validate = instance.getValidate?.();
                if (!validate) {
                  closeWithError('图形验证码结果缺失');
                  return;
                }

                resolvedRef.current = true;
                onSuccess({
                  captchaId: validate.captchaId || validate.captcha_id,
                  captchaOutput: validate.captchaOutput || validate.captcha_output,
                  lotNumber: validate.lotNumber || validate.lot_number,
                  passToken: validate.passToken || validate.pass_token,
                  genTime: validate.genTime || validate.gen_time,
                  validate: JSON.stringify(validate),
                });
                instance.hide?.();
                instance.destroy?.();
                onClose();
              });

            captchaObj.onClose?.(() => {
              if (!resolvedRef.current) {
                onClose();
              }
            });
          },
        );
      } catch (loadError) {
        if (!cancelled) {
          onError?.(loadError instanceof Error ? loadError.message : '图形验证码加载失败');
          onClose();
        }
      }
    })();

    return () => {
      cancelled = true;
      captchaRef.current?.destroy?.();
      captchaRef.current = null;
    };
  }, [onClose, onError, onSuccess, visible]);

  if (!visible) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-4">
      <div className="relative w-full max-w-[420px] rounded-2xl border border-border bg-surface p-5 shadow-card">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-ink">图形验证</h3>
            <p className="mt-1 text-sm text-muted">完成验证后继续发送短信验证码。</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-muted hover:bg-canvas hover:text-ink"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </div>

        <div className="relative min-h-[320px] overflow-hidden rounded-xl border border-border bg-[#F8FAFD]">
          {!ready ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted">
              <Loader2 className="animate-spin text-primary" size={20} />
              验证码组件加载中…
            </div>
          ) : null}
          <div id={CAPTCHA_ROOT_ID} className="min-h-[320px] w-full" />
        </div>
      </div>
    </div>,
    document.body,
  );
}
